/* =========================================================
  ____    ___    ____   _   __       __                
 |  _ |  / _ \  |  _ \ | | |   \    |   \  ___  __ __  
 |  __| | |_| | |  __/ | | | |) | o | |) |/ -_) \ V /  
 |_|\_\ |_| |_| |_|    |_| |___/    |___/ \___|  \_/  o o o  

========================================================== */

import { useState, useEffect } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import i18n from "components/Translation/Translate";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { FetchData } from "services/Request";
import * as Defi from "services/Define";
import * as Func from "services/Function";
import { setNotify, setBadge } from "redux/actions/appAction";

// Material Dashboard 2 PRO React components
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 PRO React examples
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 PRO React themes
import theme from "assets/theme";

// Material Dashboard 2 PRO React Dark Mode themes
import themeDark from "assets/theme-dark";

// Material Dashboard 2 PRO React routes
import routes from "routes";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import SignIn from "layouts/authen/SignIn";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const dispatchs = useDispatch();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const app = useSelector((state) => state[Defi.APP_STATE]);
  const { t } = useTranslation();
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  const init = () => {
    FetchData("GET", `/app/req/menu_badge`, app.token, {}).then((result) => {
      if (result.status === "OK") {
        // let temp = [];
        // result.data.map((item) => {
        //   temp = [
        //     ...temp,
        //     {
        //       id: item.id,
        //       title: `${item.title} [${Func.dateTimeDisplay(item.date_record)}]`,
        //       icon: "person",
        //     },
        //   ];
        //   return null;
        // });
        // // console.log(temp);
        // dispatchs(
        //   setNotify({
        //     count: result.count,
        //     data: temp,
        //   })
        // );
        dispatchs(
          setBadge({
            get_offer: result.count.get_offer,
            is_arrived: result.count.is_arrived,
            buyin_transaction: result.count.buyin_transaction,
            vpark_transaction: result.count.vpark_transaction,
            v_request: result.count.v_request,
            wait_for_return: result.count.wait_for_return,
            showroom_appointment: result.count.showroom_appointment,
          })
        );
      } else {
        Func.exitApp();
      }
    });

    setTimeout(function () {
      init();
    }, 60000);
  };

  const renderRoute = () =>
    routes.map((item) => {
      let found = false;
      let temp_sub = [];

      if (item.collapse) {
        item.collapse.map((sub, i) => {
          const permiss = app.menu.find(({ id }) => id === sub.key);
          if (permiss !== undefined) {
            found = true;
            temp_sub = [...temp_sub, sub];
          }

          return null;
        });
      } else {
        found = true;
      }

      return item.key === "avatar"
        ? {
            type: "collapse",
            name: app.user.name,
            key: "avatar",
            icon: <MDAvatar src={app.user.image} alt={app.user.name} size="sm" />,
            collapse: item.collapse,
          }
        : item.type === "title"
        ? item
        : found
        ? {
            type: item.type,
            name: t(item.name),
            key: item.key,
            route: item.route,
            icon: item.icon,
            collapse: temp_sub,
            noCollapse: item.noCollapse,
            component: item.component,
          }
        : {};
    });

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
    i18n.changeLanguage(app.lang);

    // if (pathname !== "/signin") init();
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const getRoutesId = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutesId(route.collapse);
      }

      if (route.route) {
        return (
          <Route exact path={`${route.route}/:id`} element={route.component} key={route.key} />
        );
      }

      return null;
    });

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <Sidenav
          color={sidenavColor}
          brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
          brandName={t(`app.name`)}
          routes={renderRoute()}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(renderRoute())}
        {getRoutesId(routes)}
        <Route exact path="/signin" element={<SignIn />} key="signin" />
        <Route path="*" element={<Navigate to="/signin" />} />
      </Routes>
    </ThemeProvider>
  );
}
